// File: src/Components/pages/about/Game.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { l } from '../../../lang/index.jsx';
import leo from 'leo-profanity';
import { supabase } from '../../../utils/supabase.js';

const COLS = 6;
const ROWS = 5;
const TOTAL_CELLS = COLS * ROWS;
const DURATION_MS = 30_000;
const MAX_ENTRIES = 5;
const MAX_NAME_LENGTH = 15;

// Ajout des mots français non couverts par le dictionnaire anglais de leo
leo.add([
    'merde', 'putain', 'connard', 'connasse', 'salope', 'pute', 'bite', 'couille',
    'encule', 'fdp', 'baise', 'baiser', 'niquer', 'nique', 'chier', 'vagin',
]);

// Normalise pour le leet speak et caractères spéciaux
function normalize(str) {
    return str.toLowerCase()
        .replace(/1/g, 'i').replace(/3/g, 'e').replace(/0/g, 'o')
        .replace(/4/g, 'a').replace(/@/g, 'a').replace(/\$/g, 's')
        .replace(/[^a-z]/g, '');
}

const NORMALIZED_WORDS = leo.list().map(normalize).filter(Boolean);

function isProfane(name) {
    const n = normalize(name);
    return NORMALIZED_WORDS.some(w => n.includes(w));
}

async function fetchScoreboard() {
    const { data, error } = await supabase
        .from('scores')
        .select('name, score, accuracy, accuracy_num')
        .order('score', { ascending: false })
        .order('accuracy_num', { ascending: false })
        .limit(MAX_ENTRIES);
    if (error) return [];
    return data || [];
}

async function submitScore(entry) {
    const { error } = await supabase
        .from('scores')
        .insert([{ name: entry.name, score: entry.score, accuracy: entry.accuracy, accuracy_num: entry.accuracy_num }]);
    if (error) throw error;
    return fetchScoreboard();
}

async function replaceScore(existingName, entry) {
    const { error } = await supabase
        .from('scores')
        .update({ score: entry.score, accuracy: entry.accuracy, accuracy_num: entry.accuracy_num })
        .eq('name', existingName);
    if (error) throw error;
    return fetchScoreboard();
}

function qualifiesForBoard(score, accNum, board) {
    if (score === 0) return false;
    if (board.length < MAX_ENTRIES) return true;
    const last = board[board.length - 1];
    if (score > last.score) return true;
    if (score === last.score && accNum > (last.accuracy_num || 0)) return true;
    return false;
}

function randIndex(exclude) {
    if (TOTAL_CELLS <= 1) return 0;
    let i;
    do {
        i = Math.floor(Math.random() * TOTAL_CELLS);
    } while (i === exclude);
    return i;
}

export default function Game({ lang, visible = false, onClose = () => {}, textMap = null }) {
    const t = (key) => textMap?.[key]?.[lang] || l(key, lang);
    const instructions = t('game-instructions');
    const startText = t('game-start-btn');
    const bestText = t('game-best');
    const restartText = t('game-restart-btn');

    const [score, setScore] = useState(0);
    const [active, setActive] = useState(null);
    const [running, setRunning] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [remaining, setRemaining] = useState(DURATION_MS / 1000);
    const [best, setBest] = useState(() => {
        try {
            const v = localStorage.getItem("aim_30s_best");
            return v ? parseInt(v, 10) : null;
        } catch (e) {
            return null;
        }
    });

    const [totalClicks, setTotalClicks] = useState(0);
    const [successfulClicks, setSuccessfulClicks] = useState(0);

    // Scoreboard global (Supabase)
    const [scoreboard, setScoreboard] = useState([]);
    const [loadingBoard, setLoadingBoard] = useState(true);
    const [showNameInput, setShowNameInput] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [nameError, setNameError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [playerRank, setPlayerRank] = useState(null);
    const [pendingEntry, setPendingEntry] = useState(null);

    // Chargement initial du classement
    useEffect(() => {
        fetchScoreboard().then(data => {
            setScoreboard(data);
            setLoadingBoard(false);
        });
    }, []);

    const handleClose = useCallback(() => {
        setRunning(false);
        setActive(null);
        setStartTime(null);
        setShowNameInput(false);
        setNameInput('');
        setNameError('');
        setSubmitError('');
        setPendingEntry(null);
        try { document.documentElement.classList.remove('popup-game-active'); } catch (e) {}
        try { onClose(); } catch (e) {}
    }, [onClose]);

    useEffect(() => {
        let id = null;
        if (running && startTime != null) {
            id = setInterval(() => {
                const elapsed = performance.now() - startTime;
                const rem = Math.max(0, (DURATION_MS - elapsed) / 1000);
                setRemaining(rem);
                if (elapsed >= DURATION_MS) {
                    setRemaining(0);
                    setRunning(false);
                    setActive(null);
                    setTimeout(() => {
                        setBest((prev) => {
                            if (prev === null || score > prev) {
                                try { localStorage.setItem("aim_30s_best", String(score)); } catch (e) {}
                                return score;
                            }
                            return prev;
                        });
                        const accNum = totalClicks === 0 ? 0 : Math.floor((successfulClicks / totalClicks) * 10000) / 100;
                        const acc = totalClicks === 0 ? '—' : `${accNum.toFixed(2)}%`;
                        // Récupère le classement à jour avant de vérifier la qualification
                        fetchScoreboard().then(freshBoard => {
                            setScoreboard(freshBoard);
                            if (qualifiesForBoard(score, accNum, freshBoard)) {
                                setPendingEntry({ score, accuracy: acc, accuracy_num: accNum });
                                setShowNameInput(true);
                            }
                        });
                    }, 0);
                }
            }, 50);
        }
        return () => {
            if (id) clearInterval(id);
        };
    }, [running, startTime, score, totalClicks, successfulClicks]);

    useEffect(() => {
        function onKey(e) {
            if (e.key === 'Escape') handleClose();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handleClose]);

    useEffect(() => {
        try {
            const cls = 'popup-game-open';
            if (visible) {
                document.documentElement.classList.add(cls);
            } else {
                document.documentElement.classList.remove(cls);
            }
        } catch (e) {}
        return () => {
            try { document.documentElement.classList.remove('popup-game-open'); } catch (e) {}
        };
    }, [visible]);

    useEffect(() => {
        try {
            const cls = 'popup-game-active';
            if (running) {
                document.documentElement.classList.add(cls);
            } else {
                document.documentElement.classList.remove(cls);
            }
        } catch (e) {}
        return () => {
            try { document.documentElement.classList.remove('popup-game-active'); } catch (e) {}
        };
    }, [running]);

    function start() {
        setScore(0);
        setRemaining(DURATION_MS / 1000);
        const now = performance.now();
        setStartTime(now);
        setRunning(true);
        setActive(randIndex(null));
        setTotalClicks(0);
        setSuccessfulClicks(0);
        setShowNameInput(false);
        setNameInput('');
        setNameError('');
        setSubmitError('');
        setPendingEntry(null);
        setPlayerRank(null);
    }

    function reset() {
        setRunning(false);
        setScore(0);
        setRemaining(DURATION_MS / 1000);
        setStartTime(null);
        setActive(null);
        setTotalClicks(0);
        setSuccessfulClicks(0);
        setShowNameInput(false);
        setNameInput('');
        setNameError('');
        setSubmitError('');
        setPendingEntry(null);
        setPlayerRank(null);
    }

    async function handleNameSubmit() {
        const trimmed = nameInput.trim();
        if (!trimmed) {
            setNameError(t('game-name-error-empty'));
            return;
        }
        if (trimmed.length > MAX_NAME_LENGTH) {
            setNameError(t('game-name-error-long'));
            return;
        }
        if (isProfane(trimmed)) {
            setNameError(t('game-name-error-profane'));
            return;
        }
        const existing = scoreboard.find(e => e.name.toLowerCase() === trimmed.toLowerCase());
        if (existing) {
            const betterScore = pendingEntry.score > existing.score ||
                (pendingEntry.score === existing.score && pendingEntry.accuracy_num > (existing.accuracy_num || 0));
            if (!betterScore) {
                setNameError(t('game-name-error-duplicate-worse'));
                return;
            }
        }
        try {
            const entry = { name: trimmed, score: pendingEntry.score, accuracy: pendingEntry.accuracy, accuracy_num: pendingEntry.accuracy_num };
            const newBoard = await (existing ? replaceScore(existing.name, entry) : submitScore(entry));
            setScoreboard(newBoard);
            const rank = newBoard.findIndex(e => e.score === entry.score && e.name === entry.name) + 1;
            setPlayerRank(rank > 0 ? rank : null);
            setShowNameInput(false);
            setNameInput('');
            setPendingEntry(null);
        } catch (err) {
            console.error('Supabase submit error:', err);
            setSubmitError(t('game-name-error-submit'));
        }
    }

    const dotClickedRef = useRef(false);

    function onDotPointerDown(idx, e) {
        if (!running) return;
        dotClickedRef.current = true;
        if (e && e.stopPropagation) e.stopPropagation();
        setTotalClicks(t => t + 1);
        if (active === idx) {
            setSuccessfulClicks(s => s + 1);
            setScore(s => s + 1);
            setActive((prev) => randIndex(prev));
        }
        setTimeout(() => { dotClickedRef.current = false; }, 0);
    }

    function onPlayGridPointerDown(e) {
        if (!running) return;
        try {
            const cx = (e && (e.clientX ?? (e.nativeEvent && e.nativeEvent.clientX)));
            const cy = (e && (e.clientY ?? (e.nativeEvent && e.nativeEvent.clientY)));
            if (typeof document !== 'undefined' && typeof document.elementFromPoint === 'function' && cx != null && cy != null) {
                const el = document.elementFromPoint(cx, cy);
                if (el && typeof el.closest === 'function' && el.closest('.dot')) {
                    return;
                }
            }
        } catch (err) {}
        setTotalClicks(t => t + 1);
    }

    function onDotKeyActivate(idx, e) {
        if (!running) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setTotalClicks(t => t + 1);
            if (active === idx) {
                setSuccessfulClicks(s => s + 1);
                setScore(s => s + 1);
                setActive((prev) => randIndex(prev));
            }
        }
    }

    const showStats = startTime !== null || score > 0;

    function onBackdropClick(e) {
        if (e.target && e.currentTarget && e.target === e.currentTarget) {
            handleClose();
        }
    }

    const accuracyText = totalClicks === 0
        ? 'Précision : —'
        : (() => {
            const pctNum = (successfulClicks / totalClicks) * 100;
            const truncated = Math.floor(pctNum * 100) / 100;
            const pctStr = truncated.toFixed(2);
            return `Précision : ${pctStr}%`;
        })();

    const popupClass = `popup ${visible ? 'popup-open' : 'popup-closed'}`;

    return (
        <div className={popupClass} onClick={onBackdropClick} aria-hidden={!visible}>
            <div className="game" onClick={(e) => e.stopPropagation()}>
                <div className="game-close cursor-event" onClick={handleClose} aria-label="Fermer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 20 20"><path fill="currentColor" d="M10 8.586L2.929 1.515L1.515 2.929L8.586 10l-7.071 7.071l1.414 1.414L10 11.414l7.071 7.071l1.414-1.414L11.414 10l7.071-7.071l-1.414-1.414L10 8.586z"/></svg>
                </div>

                <div className="game-controls">
                    {(startTime !== null || score > 0) && <div className="btn-reset small" onClick={reset}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 20 20"><path fill="currentColor" d="M14.66 15.66A8 8 0 1 1 17 10h-2a6 6 0 1 0-1.76 4.24l1.42 1.42zM12 10h8l-4 4l-4-4z"/></svg>
                    </div>}
                    {showStats && <div className="score">{score}</div>}
                    {showStats && <div className="small time">{remaining.toFixed(2)}s</div>}
                </div>

                <div className="play-grid" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }} onPointerDown={(e) => onPlayGridPointerDown(e)}>
                    {Array.from({ length: TOTAL_CELLS }, (_, i) => {
                        const isActive = active === i;
                        return (
                            <div
                                key={i}
                                className={`cell ${isActive ? "active" : ""}`}
                            >
                                {isActive ? (
                                    <div
                                        className="dot"
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => onDotKeyActivate(i, e)}
                                        onPointerDown={(e) => onDotPointerDown(i, e)}
                                      />
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                {!running && startTime === null && (
                    <div className="overlay first" style={{ pointerEvents: "auto" }}>
                        <div className="overlay-container">
                            <div className="infos">
                                <div>{instructions}</div>
                                {best !== null && <div className="small best">{bestText} <strong>{best}</strong></div>}
                            </div>
                            <div className="button-inner">
                                <button className="button" onClick={start} aria-label="Start the game">
                                    <span>{startText}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                                    </svg>
                                </button>
                            </div>
                            {!loadingBoard && scoreboard.length > 0 && (
                                <div className="scoreboard">
                                    <p className="scoreboard-title">{t('game-scoreboard')}</p>
                                    <ol>
                                        {scoreboard.map((entry, i) => (
                                            <li key={i}>
                                                <span className="sb-rank">#{i + 1}</span>
                                                <span className="sb-name">{entry.name}</span>
                                                <span className="sb-accuracy">{entry.accuracy}</span>
                                                <span className="sb-score">{entry.score}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!running && startTime !== null && remaining <= 0 && (
                    <div className="overlay replay" style={{ pointerEvents: "auto" }}>
                        <div className="overlay-container">
                            <div className="infos">
                                <div className="score">
                                    <span>Score: {score}</span>
                                    <span>{accuracyText}</span>
                                </div>
                                <div className="best">{bestText} <strong>{best !== null ? best : "-"}</strong></div>
                            </div>

                            <div className="button-inner">
                                <a className="button" onClick={start} aria-label="Restart the game">
                                    <span>{restartText}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                                    </svg>
                                </a>
                            </div>
                            {showNameInput && (
                                <div className="scoreboard-name-form">
                                    <p style={{ whiteSpace: 'pre-line' }}>{t('game-new-record')}</p>
                                    <div className="name-input-row">
                                        <input
                                            type="text"
                                            maxLength={MAX_NAME_LENGTH}
                                            value={nameInput}
                                            onChange={e => { setNameInput(e.target.value); setNameError(''); setSubmitError(''); }}
                                            onKeyDown={e => e.key === 'Enter' && handleNameSubmit()}
                                            placeholder={t('game-name-placeholder')}
                                            autoFocus
                                        />
                                        <button onClick={handleNameSubmit}>
                                            <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M4 12.6111L8.92308 17.5L20 6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                    {nameError && <p className="name-error">{nameError}</p>}
                                    {submitError && <p className="name-error">{submitError}</p>}
                                </div>
                            )}
                            {!showNameInput && scoreboard.length > 0 && (
                                <div className="scoreboard">
                                    <p className="scoreboard-title">{t('game-scoreboard')}</p>
                                    <ol>
                                        {scoreboard.map((entry, i) => (
                                            <li key={i} className={playerRank === i + 1 ? 'highlight' : ''}>
                                                <span className="sb-rank">#{i + 1}</span>
                                                <span className="sb-name">{entry.name}</span>
                                                <span className="sb-accuracy">{entry.accuracy}</span>
                                                <span className="sb-score">{entry.score}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}